import { Link } from "@tanstack/react-router";
import { MessageCircle, Mail, MapPin, ShieldCheck, CreditCard, Banknote, Phone, Building2, Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { useTranslation } from "react-i18next";
import logoAsset from "@/assets/logo.png.asset.json";
const logo = logoAsset.url;
import { WHATSAPP_NUMBER, buildWhatsAppUrl } from "@/lib/whatsapp";

const SERVICE_CITIES = [
  "Casablanca", "Marrakech", "Fez", "Tangier",
  "Agadir", "Rabat", "Essaouira", "Chefchaouen",
  "Ouarzazate", "Merzouga", "Meknes", "Tetouan",
];

const TOP_ROUTES: Array<{ label: string; href: string }> = [
  { label: "Agadir Airport to Imsouane Surf Transfer", href: "/transfers/agadir-airport-to-imsouane-surf" },
  { label: "Agadir to Taghazout Private Transfer", href: "/transfers/agadir-to-taghazout-surf" },
  { label: "Marrakech to Agadir Transfer", href: "/transfers/marrakech-to-agadir" },
  { label: "Marrakech to Essaouira Transfer", href: "/transfers/marrakech-to-essaouira" },
  { label: "Agadir Airport to Essaouira Transfer", href: "/transfers/agadir-airport-to-essaouira" },
];

const SOCIAL_LINKS: Array<{ label: string; href: string; Icon: typeof Facebook }> = [
  { label: "Facebook", href: "#", Icon: Facebook },
  { label: "Instagram", href: "#", Icon: Instagram },
  { label: "LinkedIn", href: "#", Icon: Linkedin },
  { label: "X", href: "#", Icon: Twitter },
];

// Email obfuscation: split user/domain so scrapers can't grab "user@domain" verbatim.
const EMAIL_USER = "Ceo";
const EMAIL_DOMAIN = "bachawaytravel.com";

export function Footer() {
  const { t } = useTranslation();
  const emailHref = `mailto:${EMAIL_USER}\u0040${EMAIL_DOMAIN}`;
  return (
    <footer className="border-t border-border bg-secondary/40 mt-24">
      <div className="mx-auto max-w-7xl px-6 py-14 grid gap-10 md:grid-cols-12">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3">
            <img src={logo} alt="" aria-hidden="true" width={48} height={48} className="h-12 w-12 rounded-full object-contain" loading="lazy" decoding="async" />
            <div className="font-bold text-lg">BACHA <span className="text-primary">WAY</span></div>
          </div>
          <p className="mt-4 text-sm text-muted-foreground max-w-sm">{t("footer.tagline")}</p>
          <a
            href={buildWhatsAppUrl("Hello Bacha Way — I'd like a quote for a private transfer.")}
            target="_blank" rel="noopener noreferrer"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-medium text-white hover:opacity-90"
          >
            <MessageCircle className="h-4 w-4" /> {t("footer.whatsapp")}
          </a>
          <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> {t("footer.insured")}</span>
            <span className="inline-flex items-center gap-1"><CreditCard className="h-3.5 w-3.5 text-primary" /> {t("footer.card")}</span>
            <span className="inline-flex items-center gap-1"><Banknote className="h-3.5 w-3.5 text-primary" /> {t("footer.cash")}</span>
          </div>
          <div className="mt-5 flex items-center gap-3">
            {SOCIAL_LINKS.map(({ label, href, Icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Bacha Way Travel on ${label}`}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition-colors hover:bg-primary hover:text-primary-foreground hover:border-primary"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="text-xs uppercase tracking-[0.18em] text-primary font-semibold mb-3">{t("footer.platform")}</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/for-businesses" className="text-muted-foreground hover:text-primary">{t("nav.businesses")}</Link></li>
            <li><Link to="/for-suppliers" className="text-muted-foreground hover:text-primary">{t("nav.suppliers")}</Link></li>
            <li><Link to="/for-partners" className="text-muted-foreground hover:text-primary">{t("nav.partners")}</Link></li>
            <li><Link to="/blog" className="text-muted-foreground hover:text-primary">{t("nav.blog")}</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="text-xs uppercase tracking-[0.18em] text-primary font-semibold mb-3">{t("footer.services")}</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/booking" className="text-muted-foreground hover:text-primary">{t("footer.airportTransfer")}</Link></li>
            <li><Link to="/fleet" className="text-muted-foreground hover:text-primary">{t("footer.ourFleet")}</Link></li>
            <li><Link to="/routes" className="text-muted-foreground hover:text-primary">{t("footer.popularRoutes")}</Link></li>
            <li><Link to="/about" className="text-muted-foreground hover:text-primary">{t("footer.aboutUs")}</Link></li>
            <li><Link to="/contact" className="text-muted-foreground hover:text-primary">{t("footer.contact")}</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="text-xs uppercase tracking-[0.18em] text-primary font-semibold mb-3">{t("footer.weCover")}</div>
          <ul className="grid grid-cols-2 gap-y-2 text-sm">
            {SERVICE_CITIES.map((c) => (
              <li key={c}>
                <Link
                  to="/transfer/$slug"
                  params={{ slug: c.toLowerCase().replace(/ /g, "-") }}
                  className="inline-flex items-center gap-1 text-muted-foreground hover:text-primary"
                >
                  <MapPin className="h-3 w-3 text-primary/60" /> {c}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Company information — trust block */}
      <div className="border-t border-border">
        <div className="mx-auto max-w-7xl px-6 py-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-primary font-semibold mb-3 inline-flex items-center gap-2">
              <Building2 className="h-3.5 w-3.5" /> Company
            </div>
            <div className="text-sm font-semibold text-foreground">Bacha Way Travel SARL</div>
            <div className="mt-2 flex items-start gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <span>Aourir, Agadir, Morocco</span>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-primary font-semibold mb-3">Contact</div>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="tel:+212600876933" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary">
                  <Phone className="h-4 w-4 text-primary" />
                  <span dir="ltr">+212 600 876 933 <span className="text-xs opacity-70">(Morocco)</span></span>
                </a>
              </li>
              <li>
                <a href="tel:+15416283332" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary">
                  <Phone className="h-4 w-4 text-primary" />
                  <span dir="ltr">+1 (541) 628-3332 <span className="text-xs opacity-70">(US)</span></span>
                </a>
              </li>
              <li>
                <a
                  href={emailHref}
                  className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary"
                  data-user={EMAIL_USER}
                  data-domain={EMAIL_DOMAIN}
                >
                  <Mail className="h-4 w-4 text-primary" />
                  <span>
                    {EMAIL_USER}
                    <span aria-hidden="true">&#64;</span>
                    <span className="sr-only">[at]</span>
                    {EMAIL_DOMAIN}
                  </span>
                </a>
              </li>
              <li>
                <a
                  href={buildWhatsAppUrl("Hello Bacha Way — I'd like a quote.")}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary"
                >
                  <MessageCircle className="h-4 w-4 text-primary" />
                  <span dir="ltr">WhatsApp +{WHATSAPP_NUMBER.slice(0, 3)} {WHATSAPP_NUMBER.slice(3)}</span>
                </a>
              </li>
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-primary font-semibold mb-3">Our Top Transfer Routes</div>
            <ul className="space-y-2 text-sm">
              {TOP_ROUTES.map((r) => (
                <li key={r.href}>
                  <a
                    href={r.href}
                    className="inline-flex items-start gap-2 text-muted-foreground hover:text-primary"
                  >
                    <MapPin className="h-3.5 w-3.5 text-primary/70 mt-0.5 shrink-0" />
                    <span>{r.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-border">
        <div className="mx-auto max-w-7xl px-6 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-muted-foreground">
          <div>© {new Date().getFullYear()} Bacha Way Travel SARL. {t("footer.rights")}</div>
          <div className="flex items-center gap-4">
            <Link to="/about" className="hover:text-primary">{t("footer.terms")}</Link>
            <Link to="/about" className="hover:text-primary">{t("footer.privacy")}</Link>
            <span className="opacity-60">Made in Morocco 🇲🇦</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
